import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Login } from './pages/auth/Login';
import { CompleteProfile } from './pages/auth/CompleteProfile';
import { StudentDashboard } from './pages/student/StudentDashboard';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { LandingPage } from './pages/LandingPage';
import { SuspendedAccount } from './pages/auth/SuspendedAccount';

// ── Anti-DevTools Security Hook ──
const useAntiDevTools = () => {
  useEffect(() => {
    // Block right-click
    const handleContext = (e) => e.preventDefault();

    // Block F12, Ctrl+Shift+I/J, Ctrl+U
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j')) ||
        (e.ctrlKey && (e.key === 'U' || e.key === 'u'))
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('contextmenu', handleContext);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('contextmenu', handleContext);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = ['student', 'admin', 'boss'] }) => {
  const { user, profile, loading } = useAuth();
  const currentPath = window.location.pathname;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1A1A2E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1A1A2E] font-bold">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/login" replace />;
  }

  if (profile.accountStatus === 'suspended' && currentPath !== '/suspended') {
    return <Navigate to="/suspended" replace />;
  }

  if (profile.accountStatus === 'pending') {
    if (profile.role === 'admin' || profile.role === 'boss') {
       if (currentPath !== '/admin') return <Navigate to="/admin" replace />;
    } else {
       if (currentPath !== '/student') return <Navigate to="/student" replace />;
    }
    return children;
  }

  if (!allowedRoles.includes(profile.role)) {
    if (profile.role === 'admin' || profile.role === 'boss') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/student" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already logged in AND active)
const PublicRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1A1A2E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1A1A2E] font-bold">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (user && profile) {
    const currentPath = window.location.pathname;

    if (profile.accountStatus === 'suspended' && currentPath !== '/suspended') {
      return <Navigate to="/suspended" replace />;
    }

    if (profile.accountStatus === 'active' || profile.accountStatus === 'pending') {
      const isAdminRole = profile.role === 'admin' || profile.role === 'boss';
      const targetDashboard = isAdminRole ? '/admin' : '/student';
      
      if (!currentPath.startsWith(targetDashboard)) {
        return <Navigate to={targetDashboard} replace />;
      }
    }
  }

  return children;
};

const AppContent = () => {
  const { profile } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Anti-devtools
  useAntiDevTools();

  // Light mode is now default — no toggle needed

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <PublicRoute>
            <LandingPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
          </PublicRoute>
        } />
        
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        
        <Route path="/complete-profile" element={
          <ProtectedRoute allowedRoles={['student', 'admin', 'boss']}>
            <CompleteProfile />
          </ProtectedRoute>
        } />
        
        <Route path="/suspended" element={
          <ProtectedRoute allowedRoles={['student', 'admin', 'boss']}>
            <SuspendedAccount />
          </ProtectedRoute>
        } />

        {/* Student Routes */}
        <Route path="/student" element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/student/courses/:courseId" element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/student/lectures/:lectureId" element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['admin', 'boss']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/admin/courses" element={
          <ProtectedRoute allowedRoles={['admin', 'boss']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/admin/lectures" element={
          <ProtectedRoute allowedRoles={['admin', 'boss']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/admin/payments" element={
          <ProtectedRoute allowedRoles={['admin', 'boss']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/admin/codes" element={
          <ProtectedRoute allowedRoles={['admin', 'boss']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />

        {/* 404 Redirect */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
};

export const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
};